import { IntlTFunc } from 'next-intl';

import { SimpleProductFragment } from '@/fo/api/magento';

import { utilMapSimpleProductData } from '../lib/utilMapSimpleProductData';
import {
  ConfigurableVariants,
  ProductsMindbox,
  MappedProduct,
  StockStatus
} from '../types';

/** Опция атрибута */
export interface AttributeOption {
  valueIndex: number;
  label: string;
  stockStatus: StockStatus;
  isSelected: boolean;
  product?: MappedProduct;
}

/** Группа атрибутов с их опциями */
export interface AttributeGroup {
  attributeCode: string;
  attributeLabel: string;
  options: AttributeOption[];
}

/** Результат маппинга - массив групп атрибутов */
export type UniversalVariantsResult = AttributeGroup[];

/** Мапа выбранных значений атрибутов */
export type SelectedAttributes = Record<string, number>;

/**
 * Универсальный маппер для группировки вариантов по любым атрибутам
 * Автоматически определяет все доступные атрибуты и группирует их
 */
export const universalVariantsMapper = (
  configurableVariants: ConfigurableVariants,
  t: IntlTFunc,
  mindboxProducts?: ProductsMindbox
): UniversalVariantsResult => {
  if (
    !configurableVariants ||
    !Array.isArray(configurableVariants) ||
    configurableVariants.length === 0
  ) {
    return [];
  }

  const attributeCodes = new Set<string>();

  configurableVariants
    .filter((v) => !!v && !!v.attributes && v.attributes.length > 0)
    .forEach((variant) => {
      variant?.attributes?.forEach((attr) => {
        if (attr?.code) {
          attributeCodes.add(attr.code);
        }
      });
    });

  const attributeGroups: AttributeGroup[] = Array.from(attributeCodes).map(
    (attributeCode) => {
      const optionsMap = new Map<number, AttributeOption>();

      configurableVariants
        .filter(
          (v) => !!v && !!v.product && !!v.attributes && v.attributes.length > 0
        )
        .forEach((variant) => {
          const product = variant?.product as SimpleProductFragment;
          const attr = variant?.attributes?.find(
            (a) => a?.code === attributeCode
          );

          if (
            attr &&
            attr.valueIndex !== null &&
            attr.valueIndex !== undefined
          ) {
            optionsMap.set(attr.valueIndex, {
              valueIndex: attr.valueIndex,
              label: attr.label || '',
              stockStatus: (product?.stockStatus as StockStatus) || null,
              isSelected: false,
              product: utilMapSimpleProductData(product, t, mindboxProducts)
            });
          }
        });

      return {
        attributeCode,
        attributeLabel:
          attributeCode.charAt(0).toUpperCase() + attributeCode.slice(1),
        options: Array.from(optionsMap.values())
      };
    }
  );

  return attributeGroups;
};

/**
 * Фильтрует доступные опции атрибутов на основе уже выбранных значений
 * Реализует каскадную логику: первый атрибут всегда доступен,
 * остальные фильтруются на основе предыдущих выборов
 */
export const filterAvailableOptions = (
  attributeGroups: UniversalVariantsResult,
  selectedAttributes: SelectedAttributes,
  configurableVariants: ConfigurableVariants
): UniversalVariantsResult => {
  if (!configurableVariants || !Array.isArray(configurableVariants)) {
    return attributeGroups;
  }

  return attributeGroups.map((group, index) => {
    // Первый атрибут всегда доступен полностью
    if (index === 0) {
      return group;
    }

    // Для остальных атрибутов фильтруем на основе предыдущих выборов
    const previousSelections = Object.entries(selectedAttributes).slice(
      0,
      index
    );

    if (previousSelections.length === 0) {
      return {
        ...group,
        options: []
      };
    }

    // Фильтруем варианты на основе предыдущих выборов
    const availableVariants = configurableVariants.filter((variant) => {
      return previousSelections.every(([code, valueIndex]) => {
        const attr = variant?.attributes?.find((a) => a?.code === code);
        return attr && attr.valueIndex === valueIndex;
      });
    });

    // Получаем доступные значения для текущего атрибута
    const availableValueIndexes = new Set<number>();
    availableVariants.forEach((variant) => {
      const attr = variant?.attributes?.find(
        (a) => a?.code === group.attributeCode
      );
      if (attr && attr.valueIndex !== null && attr.valueIndex !== undefined) {
        availableValueIndexes.add(attr.valueIndex);
      }
    });

    return {
      ...group,
      options: group.options.filter((option) =>
        availableValueIndexes.has(option.valueIndex)
      )
    };
  });
};

/**
 * Находит продукт по выбранным атрибутам
 */
export const findProductBySelectedAttributes = (
  selectedAttributes: SelectedAttributes,
  configurableVariants: ConfigurableVariants,
  t: IntlTFunc,
  mindboxProducts?: ProductsMindbox
): MappedProduct | null => {
  if (!configurableVariants || !Array.isArray(configurableVariants)) {
    return null;
  }

  const selectedEntries = Object.entries(selectedAttributes);
  if (selectedEntries.length === 0) {
    return null;
  }

  const matchingVariant = configurableVariants.find((variant) => {
    return selectedEntries.every(([code, valueIndex]) => {
      const attr = variant?.attributes?.find((a) => a?.code === code);
      return attr && attr.valueIndex === valueIndex;
    });
  });

  if (matchingVariant?.product) {
    const mappedProduct = utilMapSimpleProductData(
      matchingVariant.product as SimpleProductFragment,
      t,
      mindboxProducts
    );

    // Добавляем выбранные атрибуты к продукту
    return {
      ...mappedProduct,
      selectedAttributes
    };
  }

  return null;
};
